/* ============================================================
 * <f-cart-goal> — progress bar + threshold-driven auto-add/remove
 * for the cart gift reward, plus enforcement that caps each gift
 * slot at qty 1 across the cart.
 *
 * The picker UI itself lives in <f-socks-picker>, which is
 * rendered as a cart line item by snippets/cart-free-socks-picker.liquid
 * — see the second class below.
 * ============================================================ */
const PDF_CART_GIFT_DISMISS_KEY = "_pdf_cart_gift_reward_dismissed";
const PDF_CART_GIFT_MAX_COUNT = 2;
let pdfCartGiftDismissedThisView = false;

function normalizeCartGiftQuantity(value) {
  const quantity = Number(value);
  return Number.isInteger(quantity) && quantity >= 1 && quantity <= PDF_CART_GIFT_MAX_COUNT
    ? quantity
    : PDF_CART_GIFT_MAX_COUNT;
}

function normalizeCartGiftSlot(value, maxSlot = PDF_CART_GIFT_MAX_COUNT) {
  const slot = Number(value);
  return Number.isInteger(slot) && slot >= 1 && slot <= maxSlot ? slot : null;
}

function getCartGiftSlot(item, fallbackSlot = null, maxSlot = PDF_CART_GIFT_MAX_COUNT) {
  return normalizeCartGiftSlot(
    item?.properties?._cart_gift_reward_slot ||
      item?.properties?._free_socks_slot ||
      fallbackSlot,
    maxSlot
  );
}

function isCartGiftItem(item) {
  return Boolean(
    item?.properties &&
      (item.properties._cart_gift_reward === "true" ||
        item.properties._free_socks_gift === "true")
  );
}

function getCartGiftProperties(slot) {
  return {
    _cart_gift_reward: "true",
    _cart_gift_reward_slot: String(slot),
    _free_socks_gift: "true",
    _free_socks_slot: String(slot),
    "Free gift": "Change option if needed",
  };
}

if (!customElements.get("f-cart-goal")) {
  class FCartGoal extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartDrawer: ".f-cart-drawer__inner",
        leftToSpendShipping: "[data-left-to-spend-shipping]",
        leftToSpendSocks: "[data-left-to-spend-socks]",
      };

      this.confettiShow = false;
      this.shippingDone = false;
      this.socksDone = false;
      this.socksMutating = false;
      this.cartUpdateUnsubscriber = null;

      const rate = Number(window.Shopify?.currency?.rate || 1);
      this.shippingGoal = Number(this.dataset.shippingGoal || this.dataset.cartGoal) * rate;
      this.goal = Number(this.dataset.cartGoal) * rate;
      this.socksGoal = Number(this.dataset.socksGoal || 0) * rate;
      this.enableSocks = this.dataset.enableSocks === "true";
      this.socksVariantId = Number(this.dataset.socksVariantId || 0);
      this.socksProductId = Number(this.dataset.socksProductId || 0);
      this.giftQuantity = normalizeCartGiftQuantity(this.dataset.giftQuantity);

      this.money_format = window.FoxThemeSettings.money_format;
      this.enableConfetti = this.dataset.enableConfetti === "true";
    }

    isDismissed() {
      return pdfCartGiftDismissedThisView;
    }

    clearDismissal() {
      pdfCartGiftDismissedThisView = false;
      try {
        sessionStorage.removeItem(PDF_CART_GIFT_DISMISS_KEY);
      } catch (e) {}
    }

    getGiftItems(cart) {
      return (cart?.items || []).filter((item) => isCartGiftItem(item));
    }

    getMissingGiftSlots(cart) {
      const presentSlots = new Set();
      this.getGiftItems(cart).forEach((item) => {
        const slot = getCartGiftSlot(item, null, this.giftQuantity);
        if (slot) presentSlots.add(slot);
      });

      const missingSlots = [];
      for (let slot = 1; slot <= this.giftQuantity; slot += 1) {
        if (!presentSlots.has(slot)) missingSlots.push(slot);
      }
      return missingSlots;
    }

    /**
     * Walk the cart, allow one qty-1 gift line per reward slot.
     * Anything else from the configured gift product is removed via /cart/update.js.
     * Self-terminating: a canonical cart triggers no writes.
     */
    async enforceGiftQuantity(cart) {
      if (!this.socksProductId) return false;
      if (this.socksMutating) return false;

      if (!cart) {
        try {
          cart = await fetch("/cart.js").then((r) => r.json());
        } catch (e) {
          return false;
        }
      }

      const socksItems = (cart.items || []).filter(
        (i) => Number(i.product_id) === this.socksProductId
      );
      if (socksItems.length === 0) return false;

      const keptSlots = new Set();
      const updates = {};
      for (const item of socksItems) {
        const isGift = isCartGiftItem(item);
        const slot = isGift ? getCartGiftSlot(item, null, this.giftQuantity) : null;
        if (isGift && slot && !keptSlots.has(slot)) {
          keptSlots.add(slot);
          if (item.quantity > 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0;
        }
      }
      if (Object.keys(updates).length === 0) return false;

      this.socksMutating = true;
      try {
        const resp = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates,
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        this.syncRenderedCart(resp);
        return true;
      } catch (e) {
        console.error("Cart gift quantity enforce failed:", e);
        return false;
      } finally {
        this.socksMutating = false;
      }
    }

    connectedCallback() {
      if (this.enableSocks && this.socksGoal > this.shippingGoal) {
        const pct = (this.shippingGoal / this.goal) * 100;
        this.style.setProperty("--shipping-milestone", `${pct}%`);
      }

      this.updateCartGoal(
        Number(this.dataset.cartTotal),
        Number(this.dataset.cartSocksTotal || this.dataset.cartTotal)
      );

      // On mount, fetch fresh cart and run enforce + reconcile in one pass.
      // This catches the case where the section re-rendered after a cart
      // change but the new element booted with prevSocksDone=false.
      if (this.enableSocks) {
        fetch("/cart.js")
          .then((r) => r.json())
          .then(async (cart) => {
            await this.enforceGiftQuantity(cart);
            await this.reconcileGiftState(cart);
          })
          .catch(() => {});
      }

      this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        async (cartState) => {
          const subtotal = this.getCartSubtotal(cartState);
          const socksSubtotal = this.getSocksSubtotal(cartState);
          if (typeof subtotal === "number" || typeof socksSubtotal === "number") {
            this.updateCartGoal(
              typeof subtotal === "number" ? subtotal : this.cartTotal * 100,
              typeof socksSubtotal === "number" ? socksSubtotal : this.socksCartTotal * 100
            );
          } else if (!this.isDesignMode()) {
            const newCart = await window.FoxThemeCartHelpers.getState();
            const newSubtotal = this.getCartSubtotal(newCart);
            const newSocksSubtotal = this.getSocksSubtotal(newCart);
            if (typeof newSubtotal === "number" || typeof newSocksSubtotal === "number") {
              this.updateCartGoal(
                typeof newSubtotal === "number" ? newSubtotal : this.cartTotal * 100,
                typeof newSocksSubtotal === "number" ? newSocksSubtotal : this.socksCartTotal * 100
              );
            }
          }
          // /cart/add.js responses are a single line item with no top-level
          // `items` array, so without this fallback the gift line was never
          // added on threshold cross until a manual page reload. Pass null
          // and let the helpers fetch /cart.js themselves.
          const cartForReconcile =
            cartState && Array.isArray(cartState.items) ? cartState : null;
          this.enforceGiftQuantity(cartForReconcile).catch(() => {});
          this.reconcileGiftState(cartForReconcile).catch(() => {});
        }
      );

      this.confettiShow = this.socksDone || this.shippingDone;
    }

    /**
     * Compare desired gift state (present iff threshold met & not dismissed)
     * against actual cart state, and converge. Idempotent: a converged cart
     * triggers no writes. Survives section re-mounts where transition-based
     * logic loses the previous state.
     */
    async reconcileGiftState(cart) {
      if (!this.enableSocks || !this.socksVariantId) return;
      if (this.socksMutating) return;
      if (!cart) {
        try {
          cart = await fetch("/cart.js").then((r) => r.json());
        } catch (e) {
          return;
        }
      }
      const giftItems = this.getGiftItems(cart);
      if (this.socksDone && !this.isDismissed()) {
        const missingSlots = this.getMissingGiftSlots(cart);
        if (missingSlots.length > 0) {
          await this.addSocksToCart(missingSlots);
        }
      } else if (!this.socksDone && giftItems.length > 0) {
        // Threshold dropped — clear dismissal so a future re-cross adds it back
        this.clearDismissal();
        await this.removeSocksFromCart();
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
    }

    isDesignMode() {
      return Boolean(window.Shopify?.designMode || window.FoxThemeSettings?.designMode);
    }

    getCartSubtotal(cartState) {
      if (typeof cartState?.items_subtotal_price === "number") {
        return cartState.items_subtotal_price;
      }
      if (typeof cartState?.cart?.items_subtotal_price === "number") {
        return cartState.cart.items_subtotal_price;
      }
      return null;
    }

    getSocksSubtotal(cartState) {
      if (typeof cartState?.original_total_price === "number") {
        return cartState.original_total_price;
      }
      if (typeof cartState?.cart?.original_total_price === "number") {
        return cartState.cart.original_total_price;
      }
      return this.getCartSubtotal(cartState);
    }

    getCartDrawerSections() {
      const ids = [];
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        ids.push(...cartDrawer.getSectionsToRender().map((s) => s.id));
      }
      // On the cart page, also bundle the main-cart sections so cart.js
      // doesn't wipe the line items rendering an undefined section payload.
      const mainCart = document.querySelector("cart-items");
      if (mainCart && typeof mainCart.getSectionsToRender === "function") {
        ids.push(
          ...mainCart.getSectionsToRender().map((s) => s.section).filter(Boolean)
        );
      }
      return ids;
    }

    syncRenderedCart(parsedState) {
      if (!parsedState?.errors) {
        const cartDrawer = document.querySelector("cart-drawer");
        const mainCart = document.querySelector("cart-items");
        const drawerWasOpen = Boolean(cartDrawer?.drawer?.isOpen?.());

        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);

        if (cartDrawer?.renderContents && parsedState?.sections?.["cart-drawer"]) {
          cartDrawer.renderContents(parsedState, true, drawerWasOpen);
        }

        if (mainCart?.renderContents && parsedState?.sections) {
          mainCart.renderContents(parsedState);
        }
        return;
      }

      window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);
    }

    updateCartGoal(state, socksState = state) {
      this.cartTotal = state / 100;
      this.socksCartTotal = socksState / 100;

      this.shippingDone = this.cartTotal >= this.shippingGoal;
      this.socksDone = this.enableSocks && this.socksGoal > 0 && this.socksCartTotal >= this.socksGoal;
      const goalDone = this.enableSocks ? this.socksDone : this.shippingDone;

      if (!this.socksDone) {
        this.clearDismissal();
      }

      if (state > 0 || socksState > 0) {
        this.classList.remove("hidden");
      } else {
        this.classList.add("hidden");
        return;
      }

      const progressTotal = this.enableSocks ? this.socksCartTotal : this.cartTotal;
      const percent = Math.min((progressTotal * 100) / this.goal, 100);
      this.style.setProperty("--progress", `${percent}%`);
      this.dataset.total = this.cartTotal;

      this.classList.toggle("f-cartgoal--shipping-done", this.shippingDone);
      this.classList.toggle("f-cartgoal--socks-done", this.socksDone);
      this.classList.toggle("f-cartgoal--done", goalDone);
      this.classList.toggle("shakeY", goalDone);

      if (!this.shippingDone) {
        const shippingLeft = (this.shippingGoal - this.cartTotal) * 100;
        const el = this.querySelector(this.selectors.leftToSpendShipping);
        if (el) el.innerHTML = formatMoney(shippingLeft, this.money_format);
      }
      if (this.shippingDone && !this.socksDone && this.enableSocks) {
        const socksLeft = (this.socksGoal - this.socksCartTotal) * 100;
        const el = this.querySelector(this.selectors.leftToSpendSocks);
        if (el) el.innerHTML = formatMoney(socksLeft, this.money_format);
      }

      // Note: gift presence is reconciled separately via reconcileGiftState,
      // not from a prevSocksDone→socksDone transition. The cart-drawer
      // section re-renders on every cart change, which constructs a fresh
      // <f-cart-goal> element with prevSocksDone=false — transitions can't
      // detect the drop in that case. Absolute reconciliation is the only
      // approach that survives section re-mounts.

      if (goalDone && this.enableConfetti) {
        this.showConfetti();
      } else if (!goalDone) {
        this.confettiShow = false;
      }
    }

    async addSocksToCart(slots = null) {
      if (this.socksMutating) return;
      if (this.isDesignMode()) return;
      if (this.isDismissed()) return;
      this.socksMutating = true;
      try {
        const cart = await fetch("/cart.js").then((r) => r.json());
        const requestedSlots = Array.isArray(slots) && slots.length > 0
          ? slots.map((slot) => normalizeCartGiftSlot(slot, this.giftQuantity)).filter(Boolean)
          : this.getMissingGiftSlots(cart);
        const presentSlots = new Set();
        this.getGiftItems(cart).forEach((item) => {
          const slot = getCartGiftSlot(item, null, this.giftQuantity);
          if (slot) presentSlots.add(slot);
        });
        const missingSlots = requestedSlots.filter((slot) => !presentSlots.has(slot));
        if (missingSlots.length === 0) return;

        const resp = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: missingSlots.map((slot) => ({
              id: this.socksVariantId,
              quantity: 1,
              properties: getCartGiftProperties(slot),
            })),
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        this.syncRenderedCart(resp);
      } catch (e) {
        console.error("Cart gift add failed:", e);
      } finally {
        this.socksMutating = false;
      }
    }

    async removeSocksFromCart() {
      if (this.isDesignMode()) return;
      if (this.socksMutating) return;
      this.socksMutating = true;
      try {
        const cart = await fetch("/cart.js").then((r) => r.json());
        const giftItems = cart.items.filter(
          (item) =>
            isCartGiftItem(item) ||
            (this.socksProductId && Number(item.product_id) === this.socksProductId)
        );
        if (giftItems.length === 0) return;

        // Strip every line that belongs to the gift product, in one update
        const updates = {};
        for (const it of giftItems) updates[it.key] = 0;
        const resp = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates,
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        this.syncRenderedCart(resp);
      } catch (e) {
        console.error("Cart gift remove failed:", e);
      } finally {
        this.socksMutating = false;
      }
    }

    showConfetti() {
      const cartDrawer = this.closest(this.selectors.cartDrawer);
      const cartTemplate = this.closest(".cart-template");
      if (!this.confettiShow && (cartDrawer || cartTemplate)) {
        setTimeout(() => {
          if (cartDrawer) new window.FoxTheme.Confetti(cartDrawer);
          if (cartTemplate) new window.FoxTheme.Confetti(document.body);
          this.confettiShow = true;
        }, 800);
      }
    }
  }

  customElements.define("f-cart-goal", FCartGoal);
}

/* ============================================================
 * <f-socks-picker> — picker UI rendered inline as a cart line
 * item. Owns its own variants JSON, click handlers, and swap
 * logic. Each cart re-render produces a fresh element which
 * connectedCallback wires up; state lives in data-* attrs and
 * the cart line itself, so re-renders preserve state.
 * ============================================================ */
if (!customElements.get("f-socks-picker")) {
  class FSocksPicker extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        variantsJson: "[data-socks-variants]",
        colorBtn: ".f-cartgoal__color",
        sizeBtn: ".f-cartgoal__size",
        colorSelect: "[data-picker-color-select]",
        sizeSelect: "[data-picker-size-select]",
        colorSwatch: "[data-picker-color-swatch]",
        colorLabel: "[data-picker-color-label]",
        sizeLabel: "[data-picker-size-label]",
        image: "[data-picker-image]",
        status: "[data-picker-status]",
        removeBtn: "[data-picker-remove]",
      };
      this.mutating = false;
      this.lineKey = this.dataset.lineKey || "";
      this.productId = Number(this.dataset.productId || 0);
      this.slot = normalizeCartGiftSlot(this.dataset.slot) || 1;
      this.colorIndex = Number(this.dataset.colorIndex);
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.variants = [];

      const json = this.querySelector(this.selectors.variantsJson);
      if (json) {
        try {
          this.variants = JSON.parse(json.textContent);
        } catch (e) {
          this.variants = [];
        }
      }

      // Selection comes from the live cart line's variant
      const currentVariantId = Number(this.dataset.currentVariantId || 0);
      const currentVariant = this.variants.find((v) => v.id === currentVariantId);
      this.selectedColor = currentVariant && this.colorIndex >= 0
        ? currentVariant[`option${this.colorIndex + 1}`]
        : null;
      this.selectedSize = currentVariant && this.sizeIndex >= 0
        ? currentVariant[`option${this.sizeIndex + 1}`]
        : null;
    }

    connectedCallback() {
      this.bindEvents();
      this.refreshUI();
    }

    getColorHex(color) {
      const value = String(color || "").toLowerCase().trim();
      const colors = {
        black: "#000000",
        midnight: "#172033",
        white: "#ffffff",
        "petal pink": "#f3c9cc",
        "soft mauve": "#b48f8c",
        navy: "#0f1d3a",
        cream: "#f5ecd9",
        sage: "#8a9a82",
        grey: "#8c8c8c",
        gray: "#8c8c8c",
      };
      return colors[value] || "#cccccc";
    }

    snapToAvailableSize() {
      if (this.colorIndex < 0 || this.sizeIndex < 0) return;
      const variant = this.resolveVariant(this.selectedColor, this.selectedSize);
      if (variant && variant.available) return;
      const firstForColor = this.variants.find(
        (x) => x[`option${this.colorIndex + 1}`] === this.selectedColor && x.available
      );
      if (firstForColor) {
        this.selectedSize = firstForColor[`option${this.sizeIndex + 1}`];
      }
    }

    bindEvents() {
      this.querySelectorAll(this.selectors.colorBtn).forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.selectedColor = btn.dataset.pickerColor;
          // Auto-snap size if current size unavailable in this color
          this.snapToAvailableSize();
          this.refreshUI();
          this.applyChange();
        });
      });
      this.querySelectorAll(this.selectors.sizeBtn).forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.selectedSize = btn.dataset.pickerSize;
          this.refreshUI();
          this.applyChange();
        });
      });
      const colorSelect = this.querySelector(this.selectors.colorSelect);
      if (colorSelect) {
        colorSelect.addEventListener("change", () => {
          this.selectedColor = colorSelect.value;
          this.snapToAvailableSize();
          this.refreshUI();
          this.applyChange();
        });
      }
      const sizeSelect = this.querySelector(this.selectors.sizeSelect);
      if (sizeSelect) {
        sizeSelect.addEventListener("change", () => {
          this.selectedSize = sizeSelect.value;
          this.refreshUI();
          this.applyChange();
        });
      }
      const removeBtn = this.querySelector(this.selectors.removeBtn);
      if (removeBtn) {
        removeBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.dismiss();
        });
      }
    }

    resolveVariant(color, size) {
      return this.variants.find((v) => {
        const cMatch = this.colorIndex < 0 || color == null || v[`option${this.colorIndex + 1}`] === color;
        const sMatch = this.sizeIndex < 0 || size == null || v[`option${this.sizeIndex + 1}`] === size;
        return cMatch && sMatch;
      });
    }

    refreshUI() {
      this.querySelectorAll(this.selectors.colorBtn).forEach((btn) => {
        btn.classList.toggle("is-selected", btn.dataset.pickerColor === this.selectedColor);
      });
      this.querySelectorAll(this.selectors.sizeBtn).forEach((btn) => {
        const size = btn.dataset.pickerSize;
        const v = this.resolveVariant(this.selectedColor, size);
        btn.classList.toggle("is-selected", size === this.selectedSize);
        btn.classList.toggle("is-unavailable", !v || !v.available);
      });

      const colorSelect = this.querySelector(this.selectors.colorSelect);
      if (colorSelect && this.selectedColor != null) {
        colorSelect.value = this.selectedColor;
      }
      const sizeSelect = this.querySelector(this.selectors.sizeSelect);
      if (sizeSelect && this.selectedSize != null) {
        sizeSelect.value = this.selectedSize;
        Array.from(sizeSelect.options).forEach((option) => {
          const variant = this.resolveVariant(this.selectedColor, option.value);
          option.disabled = !variant || !variant.available;
        });
      }
      const colorSwatch = this.querySelector(this.selectors.colorSwatch);
      if (colorSwatch) {
        colorSwatch.style.backgroundColor = this.getColorHex(this.selectedColor);
        colorSwatch.classList.toggle(
          "is-light",
          ["white", "cream", "petal pink"].includes(String(this.selectedColor || "").toLowerCase())
        );
      }

      const colorLabel = this.querySelector(this.selectors.colorLabel);
      if (colorLabel) colorLabel.textContent = this.selectedColor || "—";
      const sizeLabel = this.querySelector(this.selectors.sizeLabel);
      if (sizeLabel) sizeLabel.textContent = this.selectedSize || "—";

      const variant = this.resolveVariant(this.selectedColor, this.selectedSize);
      const img = this.querySelector(this.selectors.image);
      if (img && variant && variant.image) {
        img.src = variant.image;
        img.removeAttribute("hidden");
      }

      const status = this.querySelector(this.selectors.status);
      if (status && variant) {
        const defaultStatus = this.dataset.pickerStatus || "Free gift: Change option if needed";
        status.textContent = this.classList?.contains?.("f-cartgoal__picker--reward-card")
          ? defaultStatus
          : "Free gift: " + variant.title;
      }
    }

    setStatus(text) {
      const el = this.querySelector(this.selectors.status);
      if (el) el.textContent = text || "";
    }

    getCartDrawerSections() {
      const ids = [];
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        ids.push(...cartDrawer.getSectionsToRender().map((section) => section.id));
      }
      const mainCart = document.querySelector("cart-items");
      if (mainCart && typeof mainCart.getSectionsToRender === "function") {
        ids.push(
          ...mainCart.getSectionsToRender().map((section) => section.section).filter(Boolean)
        );
      }
      return ids;
    }

    syncRenderedCart(parsedState) {
      if (!parsedState?.errors) {
        const cartDrawer = document.querySelector("cart-drawer");
        const mainCart = document.querySelector("cart-items");
        const drawerWasOpen = Boolean(cartDrawer?.drawer?.isOpen?.());

        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);

        if (cartDrawer?.renderContents && parsedState?.sections?.["cart-drawer"]) {
          cartDrawer.renderContents(parsedState, true, drawerWasOpen);
        }

        if (mainCart?.renderContents && parsedState?.sections) {
          mainCart.renderContents(parsedState);
        }
        return;
      }

      window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);
    }

    async applyChange() {
      if (this.mutating) return;
      const variant = this.resolveVariant(this.selectedColor, this.selectedSize);
      if (!variant || !variant.available) {
        this.setStatus("That option is unavailable — try another size.");
        return;
      }
      // Already on the right variant — nothing to do
      if (Number(this.dataset.currentVariantId) === variant.id) return;

      // Picker click clears any prior dismissal
      try {
        pdfCartGiftDismissedThisView = false;
        sessionStorage.removeItem(PDF_CART_GIFT_DISMISS_KEY);
      } catch (e) {}

      this.mutating = true;
      this.setStatus("Updating your free gift...");
      try {
        // Replace only this reward slot. Any other reward slot must remain intact.
        const cart = await fetch("/cart.js").then((r) => r.json());
        const giftItems = cart.items.filter(
          (it) =>
            it.key === this.lineKey ||
            (
              isCartGiftItem(it) &&
              getCartGiftSlot(it) === this.slot
            ) ||
            (
              this.productId &&
              Number(it.product_id) === this.productId &&
              !isCartGiftItem(it)
            )
        );
        if (giftItems.length > 0) {
          const updates = {};
          for (const it of giftItems) updates[it.key] = 0;
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates }),
          }).then((r) => r.json());
        }
        const resp = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: variant.id,
            quantity: 1,
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
            properties: getCartGiftProperties(this.slot),
          }),
        }).then((r) => r.json());
        this.syncRenderedCart(resp);
        // After this, the cart drawer re-renders and a fresh <f-socks-picker>
        // takes over — this instance gets disconnected.
      } catch (e) {
        console.error("Picker swap failed:", e);
        this.setStatus("Couldn't update — please try again.");
      } finally {
        this.mutating = false;
      }
    }

    async dismiss() {
      if (this.mutating) return;
      this.mutating = true;
      try {
        pdfCartGiftDismissedThisView = true;
        sessionStorage.removeItem(PDF_CART_GIFT_DISMISS_KEY);
      } catch (e) {}
      try {
        const resp = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: this.lineKey,
            quantity: 0,
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        this.syncRenderedCart(resp);
      } catch (e) {
        console.error("Picker dismiss failed:", e);
      } finally {
        this.mutating = false;
      }
    }
  }

  customElements.define("f-socks-picker", FSocksPicker);
}
